class ScrollDirectionTracker {
  constructor(options = {}) {
    this.scrollContainerSelector = options.scrollContainerSelector || null;
    this.threshold = options.threshold != null ? options.threshold : 8;
    this.topOffset = options.topOffset != null ? options.topOffset : 20;
    this.bottomOffset = options.bottomOffset != null ? options.bottomOffset : 4;
    this.minScrollableHeight =
      options.minScrollableHeight != null ? options.minScrollableHeight : 10;
    this.onChange = options.onChange || (() => {});
    this.onScrolledChange = options.onScrolledChange || (() => {});

    this.isHidden = false;
    this.hasScrolled = false;
    this.lastScrollY = 0;
    this.lastFlipTime = 0;
    this.ticking = false;
    this.target = null;
    this.rafId = null;
    this.attachAttempts = 0;
    this.running = false;

    this._onScroll = this._onScroll.bind(this);
    this._update = this._update.bind(this);
    this._tryAttach = this._tryAttach.bind(this);
  }

  start() {
    if (this.running) return;
    this.running = true;
    this._tryAttach();
  }

  stop() {
    if (!this.running) return;
    this.running = false;
    this._detach();
    if (this.isHidden) {
      this.isHidden = false;
      this.onChange(false);
    }
  }

  _setHidden(next) {
    if (next === this.isHidden) return;
    const now = Date.now();
    if (now - this.lastFlipTime < 100) return;
    this.isHidden = next;
    this.lastFlipTime = now;
    this.onChange(next);
  }

  _setScrolled(next) {
    if (next === this.hasScrolled) return;
    this.hasScrolled = next;
    this.onScrolledChange(next);
  }

  _getScrollTop() {
    if (this.target && this.target !== window) {
      return this.target.scrollTop || 0;
    }
    return (
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0
    );
  }

  _getMaxScroll() {
    if (this.target && this.target !== window) {
      return (this.target.scrollHeight || 0) - (this.target.clientHeight || 0);
    }
    return (
      (document.documentElement.scrollHeight || 0) -
      (window.innerHeight || 0)
    );
  }

  _isScrollable() {
    return this._getMaxScroll() > this.topOffset + this.minScrollableHeight;
  }

  _update() {
    const currentScrollY = this._getScrollTop();
    const diff = currentScrollY - this.lastScrollY;
    this.lastScrollY = currentScrollY > 0 ? currentScrollY : 0;
    this.ticking = false;

    // If content fits in viewport, don't engage hide-on-scroll —
    // hiding bars on a short list creates a scroll dead-zone.
    if (!this._isScrollable()) {
      this._setHidden(false);
      this._setScrolled(false);
      return;
    }

    const maxScroll = this._getMaxScroll();

    // Ignore overscroll bounce
    if (currentScrollY < 0) return;
    if (maxScroll > 0 && currentScrollY > maxScroll - this.bottomOffset) return;

    if (currentScrollY <= this.topOffset) {
      this._setHidden(false);
      this._setScrolled(false);
      return;
    }

    this._setScrolled(true);

    if (Math.abs(diff) < this.threshold) return;

    this._setHidden(diff > 0);
  }

  _onScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(this._update);
      this.ticking = true;
    }
  }

  _attach() {
    this.target = this.scrollContainerSelector
      ? document.querySelector(this.scrollContainerSelector)
      : window;

    if (!this.target) return false;

    this.lastScrollY = this._getScrollTop();
    this.target.addEventListener("scroll", this._onScroll, { passive: true });
    return true;
  }

  _detach() {
    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.target) {
      this.target.removeEventListener("scroll", this._onScroll);
      this.target = null;
    }
  }

  _tryAttach() {
    const MAX_ATTEMPTS = 30;
    if (!this.running) return;
    if (this._attach()) {
      this.attachAttempts = 0;
      return;
    }
    this.attachAttempts += 1;
    if (this.attachAttempts < MAX_ATTEMPTS) {
      this.rafId = window.requestAnimationFrame(this._tryAttach);
    }
  }
}

export default ScrollDirectionTracker;